import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Apollo, gql } from 'apollo-angular';
import { MessageModule } from 'primeng/message';
import { firstValueFrom } from 'rxjs';
import { VInputFieldComponent } from 'src/app/core/components/ui-kit/input-field/input-field.component';

import { CheckboxModule } from 'primeng/checkbox';
import { VButtonV2Component } from 'src/app/core/components/ui-kit/buttons/button-v2/button.component';
import { VCardComponent } from 'src/app/core/components/ui-kit/card/card.component';
import { VInputV2Component } from 'src/app/core/components/ui-kit/input/v2/input.component';
import { SharedModule } from 'src/app/core/shared.module';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { ResetOnboardingAction } from 'src/app/store/onboarding/onboarding.actions';
import { LoadOrganizationAction } from 'src/app/store/organization/organization.actions';
import { OrganizationStateModule } from 'src/app/store/organization/organization.state.module';
import { SettingUpdateOrganizationMemberMeAction } from 'src/app/store/settings/settings.actions';
import { LoadSubscriptionAction } from 'src/app/store/subscription/subscription.actions';
import { SubscriptionState } from 'src/app/store/subscription/subscription.state';
import {
  LoadUserAction,
  LogoutAction,
  SelectOrganizationAction,
} from 'src/app/store/user/user.actions';
import { VBottomGradientWrapperComponent } from '../components/bottom-gradient/bottom-gradient.component';
import { VLogoHeaderComponent } from '../components/logo-header/logo-header.component';

const CREATE_ORGANIZATION_MUTATION = gql`
  mutation CreateOrganization($name: String!) {
    createOrganization(newOrganization: { name: $name }) {
      id
    }
  }
`;

@Component({
  selector: 'app-page-new-organization',
  imports: [
    SharedModule,
    MessageModule,
    ReactiveFormsModule,
    VInputFieldComponent,
    VBottomGradientWrapperComponent,
    VLogoHeaderComponent,
    VCardComponent,
    VButtonV2Component,
    VInputV2Component,
    OrganizationStateModule,
    CheckboxModule,
  ],
  templateUrl: './new-organization.component.html',
})
export class NewOrganizationPageComponent {
  private apollo = inject(Apollo);
  private router = inject(Router);
  private store = inject(Store);
  private translateService = inject(TranslationService);

  newOrgForm = new FormGroup({
    orgName: new FormControl('', Validators.required),
    acceptTerms: new FormControl(false, Validators.requiredTrue),
  });

  isSubmitting = false;

  createOrganizationError?: string;

  onFormSubmit() {
    if (!this.newOrgForm.valid) {
      return;
    }

    this.createOrganizationError = undefined;
    this.newOrgForm.disable();

    this.isSubmitting = true;

    this.apollo
      .mutate<{ createOrganization: { id: string } }>({
        mutation: CREATE_ORGANIZATION_MUTATION,
        variables: {
          name: this.newOrgForm.value.orgName,
        },
      })
      .subscribe({
        next: res => {
          const actions = [
            new SelectOrganizationAction(res.data!.createOrganization.id),
            new LoadUserAction(),
            new LoadOrganizationAction(),
            new SettingUpdateOrganizationMemberMeAction(
              this.translateService.currentTimezone,
              this.translateService.currentLocale
            ),
            new ResetOnboardingAction(),
          ];

          // Create a timer that resolves after 3 seconds
          const timer = new Promise(resolve => setTimeout(resolve, 3000));

          const checkSubscription = async (
            attempt = 1,
            delay = 500
          ): Promise<void> => {
            if (attempt > 10) {
              return;
            }

            await this.store.dispatch(new LoadSubscriptionAction()).toPromise();
            const canChange = this.store.selectSnapshot(
              SubscriptionState.canChangeSubscription
            );

            if (!canChange) {
              await new Promise(resolve => setTimeout(resolve, delay));
              return checkSubscription(attempt + 1, delay * 2);
            }
          };

          // Wait for both the actions and the timer to complete
          Promise.all([
            firstValueFrom(this.store.dispatch(actions)),
            checkSubscription(),
            timer,
          ]).then(() => {
            this.router.navigate(['/onboarding']);
          });
        },
        error: err => {
          this.isSubmitting = false;
          this.createOrganizationError = err.toString();
          this.newOrgForm.enable();
        },
      });
  }

  logout() {
    this.store.dispatch(new LogoutAction());
  }
}
